export const GERMAN_BASE = 720

const CARNIVORE_VAL = 2420
const FLEXITARIAN_VAL = 1495
const VEGETARIAN_VAL = 1430
const VEGAN_VAL = 1160

const HEATING_PUMP = 25.7
const HEATING_DISTRICT = 21.3
const HEATING_WOOD = 4.21
const HEATING_GAS = 34.2
const HEATING_OIL = 42.1
const HEATING_COAL = 56.8
const HEATING_ELECTRICITY = 91.06

const CAR_PER_KM = 0.0901
// 10.000 km = 1.28t CO2 https://www.vda.de/en/topics/environment-and-climate/co2-development/co2-trends-in-germany.html
// expecting that the person does not always drive alone https://www.eea.europa.eu/data-and-maps/indicators/occupancy-rates-of-passenger-vehicles/occupancy-rates-of-passenger-vehicles
// 0.128 / 1.42 passenders per ride  = 0.0901

const ELECTRICITY_NORMAL = 1074 // simplified version, per head 2000kwh * 0.537 g/kWh http://www.oekoheizstrom.de/wieviel-co2-emissionen-pro-kwh-kilowattstunde-strom-2344/
const ELECTRICITY_ECO = 0

export const QUESTIONS = [
  {
    number: 0,
    question:
      "How many short flights (<3h) did you take in the last 12 months?",
    description:
      "We assume that you made mostly round trips, so 1 short flight would be for example: Berlin - London and London - Berlin",
    explanation: "Flying causes lots of carbon emissions...",
    type: "inputNumber",
    factor: 700,
    message: "Please enter a value!",
    placeholder: "Something",
    label: "Short flights",
    initialValue: 1,
    category: "Mobility",
    results: {
      algorithm: "smaller",
      values: [
        {
          value: 3,
          title: "Short flights: less is hardly possible",
          text:
            "You rarely take short term or domestic flights, looks like you prefer train travel. Amazing!",
        },
        {
          value: 5,
          title: "Short flights: alright but improvable",
          text:
            "You sometimes travel by airplane for short distances. Try to switch to trains or long-distance busses instead.",
        },
        {
          title: "Short flights: Improvement is absolutely necessary",
          text:
            "Take it easy on your flight routes: there are great destinations you can reach by train. Meetings can often be combined or held online via Zoom or Google Hangouts.",
        },
      ],
    },
  },
  {
    number: 1,
    question: "How many long flights (>3h) did you take in the last 12 months?",
    description:
      "We assume that you made mostly round trips, so 1 long flight would be for example: Berlin - San Francisco and San Francisco - Berlin",
    explanation: "Flying causes lots of carbon emissions...",
    type: "inputNumber",
    factor: 2000,
    message: "Please enter a value!",
    placeholder: "Something",
    label: "Long flights",
    initialValue: 0,
    category: "Mobility",
    results: {
      algorithm: "smaller",
      values: [
        {
          value: 1,
          title: "Long flights: Great job!",
          text: "You did not take any long-distance flights! That is amazing.",
        },
        {
          value: 3,
          title: "Long flights: you can do better",
          text:
            "A long distance journey emits up to 4 t of CO2 per journey. Consider whether those flights are really necessary. Check for alternative destinations for vacations or try holding conferences online via Zoom or Google Hangouts.",
        },
        {
          title: "Long flights: you must change your behaviour",
          text:
            "Whether it is for business or personal reasons, you need to drastically reduce your long-haul flights. Find alternative destinations for vacations and try to merge appointsments or hold them online via Zoom or Google Hangouts.",
        },
      ],
    },
  },
  {
    number: 2,
    question: "How many km did you drive by car in the last 12 months?",
    explanation: "Cars cause lots of carbon emissions...",
    type: "inputNumber",
    factor: CAR_PER_KM,
    message: "Please enter a value!",
    placeholder: "Something",
    label: "Km by car",
    initialValue: 10000,
    category: "Mobility",
    results: {
      algorithm: "smaller",
      values: [
        {
          value: 2000,
          title: "Cars: all is well!",
          text:
            "You mostly move around with alternative means of transport and therefore hardly emit any emissions - keep up the good work.",
        },
        {
          value: 5000,
          title: "Cars: alternatives wanted",
          text:
            "The exhaust fumes from your car cause considerable damage to the environment and to yourself. Try to cover some distances by bicycle or public transport.",
        },
        {
          title: "Cars: you must change something here",
          text:
            "The car is your main mean of transport. Have you ever thought about car sharing or an electric car powered by green electricity?",
        },
      ],
    },
  },
  {
    number: 3,
    question: "What best describes your diet?",
    explanation: "Meat and dairy products cause lots of carbon emissions...",
    type: "radio",
    message: "Please enter a value!",
    placeholder: "Something",
    initialValue: "normal",
    category: "Diet",
    options: [
      { title: "Carnivore (regularly meat)", value: CARNIVORE_VAL },
      { title: "Flexitarian (rarely meat)", value: FLEXITARIAN_VAL },
      { title: "Vegetarian", value: VEGETARIAN_VAL },
      { title: "Vegan", value: VEGAN_VAL },
    ],
    results: {
      algorithm: "equals",
      values: [
        {
          value: CARNIVORE_VAL,
          title:
            "Diet: Meat and dairy cause most of carbon emissions considering nutrition",
          text:
            "Reduce excessive meat consumption at all costs. Switch to poultry, especially for red meat, and ensure that the quality is appropriate. Organic meat from regional breeding is ideal.",
        },
        {
          value: FLEXITARIAN_VAL,
          title: "Diet: Alright but still improvable",
          text:
            "Not everyone has to be vegetarian, but make sure that you buy your meat locally and organically in order to save the maximum amount of emissions.",
        },
        {
          value: VEGETARIAN_VAL,
          title: "Diet: You are doing it right",
          text:
            "Being vegetarian already saves almost all emissions in terms of nutrition. Good job!",
        },
        {
          value: VEGAN_VAL,
          title: "Diet: Great!",
          text:
            "Being vegan saves most of the emissions when it comes to nutrition. Here you are already doing everything right.",
        },
      ],
    },
  },
  {
    number: 4,
    question: "What is your housing situation?",
    explanation: "Heating our houses causes carbon emissions.",
    type: "housing",
    factor: 48,
    message: "Please enter a value!",
    placeholder: "Something",
    initialValue: { flatmates: 1, flatsize: 100 },
    options: {
      heating: [
        { title: "Coal", value: HEATING_COAL },
        { title: "Oil", value: HEATING_OIL },
        { title: "Gas", value: HEATING_GAS },
        { title: "Wood", value: HEATING_WOOD },
        { title: "District Heating (Fernwärme)", value: HEATING_DISTRICT },
        { title: "Heat pump (Wärmepumpe)", value: HEATING_PUMP },
        { title: "Electricity", value: HEATING_ELECTRICITY },
      ],
    },
    category: "Housing",
    results: {
      algorithm: "housing",
      values: [
        {
          value: HEATING_COAL,
          title:
            "Heating: you are using the most environmentally harmful technology",
          text:
            "This outdated method causes immense damage to the environment. You should urgently switch to a more sustainable technology, for example wood pellets.",
        },
        {
          value: HEATING_OIL,
          title: "Heating: Improve your heating system",
          text:
            "When heating with oil you emit 318 grams of co2 per kWh. Definitely consider switching to another form of heating for example wood pellets.",
        },
        {
          value: HEATING_GAS,
          title: "Heating: alright but improvable",
          text:
            "Even though natural gas has the smallest CO2 balance among fossil fuels, it is still not environmentally friendly. Only renewable energy such as ecogas is really suitable for the environment.",
        },
        {
          value: HEATING_WOOD,
          title: "Heating: great job!",
          text: "Pellet heating is the most environmentally friendly option.",
        },
        {
          value: HEATING_DISTRICT,
          title: "Heating: not too bad!",
          text:
            "District Heating (Fernwärme) is much more efficient than traditional coal, oil or gas heating since they recover otherwise wasted thermal energy for heating. Unfortunately, in most cases the energy for decentral district heating also comes from fossil ressources.",
        },
        {
          value: HEATING_PUMP,
          title: "Heating: not too bad!",
          text:
            "Heat pumps (Blockheizkraftwerke - BHKW) are more efficient than traditional coal, oil or gas heating since they recover otherwise wasted thermal energy for heating.",
        },
        {
          value: HEATING_ELECTRICITY,
          title: "Heating: pretty bad!",
          text:
            "Heating based on electricity is a very inefficient way to heat your house. Consider moving to a more efficient type of heating or make sure to limit the usage of your heating to an absolute minimum.",
        },
      ],
    },
  },
  {
    number: 5,
    question:
      "How much money do you spend on cloths, furniture, electronic gadgets and free time activities per month?",
    explanation: "Consumption causes carbon emissions...",
    type: "inputNumberEuro",
    factor: 9.375,
    message: "Please enter a value!",
    placeholder: "Something",
    label: "Monthly consumption",
    initialValue: 300,
    category: "Consumption",
    results: {
      algorithm: "smaller",
      values: [
        {
          value: 200,
          title:
            "Consumption: Very good, here you have everything under control.",
          text:
            "Your consumption behaviour is already greatly reduced and you are saving CO2 and resources. Here you are doing really well.",
        },
        {
          value: 400,
          title: "Consumption: still room for improvement...",
          text:
            "Even though you watch your expenses, there is still some room for improvement. Consider whether you really need the product or simply borrow it. Community use is always a resource-saving alternative.",
        },
        {
          title: "Consumption: reduction is a must-do",
          text:
            "Especially your consumption emits many greenhouse gases. You will find many products in perfect condition second hand. So pay attention to resource-saving purchasing behaviour and quality. ",
        },
      ],
    },
  },
  {
    number: 6,
    question: "What kind of energy do you use?",
    explanation: "Electricity is produced by ...",
    type: "radioEnergy",
    message: "Please enter a value!",
    placeholder: "Something",
    initialValue: "normal",
    category: "Energy",
    options: [
      { title: "Normal", value: ELECTRICITY_NORMAL },
      { title: "Green Energy", value: ELECTRICITY_ECO },
    ],
    results: {
      algorithm: "equals",
      values: [
        {
          value: ELECTRICITY_ECO,
          title: "Energy: at it's best",
          text:
            "Your electricity comes from 100% renewable resources? Great, then you're already doing everything right here.",
        },
        {
          value: ELECTRICITY_NORMAL,
          title: "Energy: switch to green electricity at all costs",
          text:
            "60% of the German electricity mix consists of fossil energy sources and nuclear power. Switching to a green power provider, such as Greenpeace Energy, guarantees you energy from 100% renewable resources.",
        },
      ],
    },
  },
  {
    number: 7,
    question:
      "Adding a baseline for fresh water, public services like waste management and more",
    description: `Carbon gets produced in almost everything we do. For simplicity, we won't ask for every detail. But we need to add a baseline of ${GERMAN_BASE}kg of carbon emissions.`,
    explanation: "",
    type: "fixed",
    message: "Please enter a value!",
    placeholder: "Something",
    initialValue: true,
    factor: 720,
    category: "General",
    options: [{ title: "Average", value: 1 }],
    results: {
      algorithm: "none",
    },
  },
]

export const AVG_FOOTPRINTS = {
  de: 9,
  world: 5,
}
